// Shared vendor data for lookups across the application
// This provides a consistent vendor list matching VendorManagement's data

import {
  Vendor,
  VendorStatus,
  VendorType,
  VendorAccount,
} from '../types/vendor'

export interface VendorLookupItem {
  id: string
  name: string
  code: string
  type: string
  country: string
}

// Vendor data matching VendorManagement's generateVendors()
export const sampleVendors: VendorLookupItem[] = [
  {
    id: 'vendor-001',
    name: 'Office Depot',
    code: 'OD-001',
    type: 'Supplier',
    country: 'United States',
  },
  {
    id: 'vendor-002',
    name: 'Dell Technologies',
    code: 'DELL-001',
    type: 'IT Equipment',
    country: 'United States',
  },
  {
    id: 'vendor-003',
    name: 'PrintWorks Inc.',
    code: 'PW-001',
    type: 'Printing Services',
    country: 'Canada',
  },
  {
    id: 'vendor-004',
    name: 'Acme Construction',
    code: 'ACM-001',
    type: 'Contractor',
    country: 'United States',
  },
  {
    id: 'vendor-005',
    name: 'Global Logistics Partners',
    code: 'GLP-001',
    type: 'Distributor',
    country: 'United Kingdom',
  },
  {
    id: 'vendor-006',
    name: 'TechSupply Co.',
    code: 'TSC-001',
    type: 'Supplier',
    country: 'Germany',
  },
  {
    id: 'vendor-007',
    name: 'Green Energy Solutions',
    code: 'GES-001',
    type: 'Service Provider',
    country: 'United States',
  },
  {
    id: 'vendor-008',
    name: 'Pacific Manufacturing',
    code: 'PMF-001',
    type: 'Manufacturer',
    country: 'Japan',
  },
  {
    id: 'vendor-009',
    name: 'Metro Cleaning Services',
    code: 'MCS-001',
    type: 'Service Provider',
    country: 'United States',
  },
  {
    id: 'vendor-010',
    name: 'Elite Consulting Group',
    code: 'ECG-001',
    type: 'Consultant',
    country: 'United States',
  },
  {
    id: 'vendor-011',
    name: 'Northern Supplies Ltd',
    code: 'NSL-001',
    type: 'Supplier',
    country: 'Canada',
  },
  {
    id: 'vendor-012',
    name: 'Apex Security Systems',
    code: 'ASS-001',
    type: 'Service Provider',
    country: 'United States',
  },
  {
    id: 'vendor-013',
    name: 'Continental Foods',
    code: 'CFD-001',
    type: 'Supplier',
    country: 'France',
  },
  {
    id: 'vendor-014',
    name: 'Digital Marketing Pro',
    code: 'DMP-001',
    type: 'Service Provider',
    country: 'United Kingdom',
  },
  {
    id: 'vendor-015',
    name: 'Industrial Parts Warehouse',
    code: 'IPW-001',
    type: 'Distributor',
    country: 'United States',
  },
  {
    id: 'vendor-016',
    name: 'Swift Transportation',
    code: 'SWT-001',
    type: 'Service Provider',
    country: 'United States',
  },
  {
    id: 'vendor-017',
    name: 'Premium Office Furniture',
    code: 'POF-001',
    type: 'Supplier',
    country: 'Italy',
  },
  {
    id: 'vendor-018',
    name: 'CloudTech Solutions',
    code: 'CTS-001',
    type: 'Service Provider',
    country: 'United States',
  },
  {
    id: 'vendor-019',
    name: 'Reliable Maintenance Co.',
    code: 'RMC-001',
    type: 'Contractor',
    country: 'United States',
  },
  {
    id: 'vendor-020',
    name: 'Global Trade Partners',
    code: 'GTP-001',
    type: 'Distributor',
    country: 'China',
  },
  {
    id: 'vendor-021',
    name: 'Melbourne Office Solutions Pty Ltd',
    code: 'MOS-001',
    type: 'Supplier',
    country: 'Australia',
  },
]

// Country ID mapping
const countryMap: Record<string, { id: string; code: string }> = {
  'United States': { id: 'country-US', code: 'US' },
  Canada: { id: 'country-CA', code: 'CA' },
  'United Kingdom': { id: 'country-GB', code: 'GB' },
  Australia: { id: 'country-AU', code: 'AU' },
  Germany: { id: 'country-DE', code: 'DE' },
  France: { id: 'country-FR', code: 'FR' },
  Japan: { id: 'country-JP', code: 'JP' },
  China: { id: 'country-CN', code: 'CN' },
  Italy: { id: 'country-IT', code: 'IT' },
}

// Map string vendor type to VendorType enum
const typeMap: Record<string, VendorType> = {
  Supplier: VendorType.Supplier,
  'IT Equipment': VendorType.Supplier,
  'Printing Services': VendorType.ServiceProvider,
  Contractor: VendorType.Contractor,
  Distributor: VendorType.Distributor,
  'Service Provider': VendorType.ServiceProvider,
  Manufacturer: VendorType.Manufacturer,
  Consultant: VendorType.Consultant,
  Other: VendorType.Other,
}

// Mock VendorAccount data for complex vendors
// Most vendors have no accounts (simple vendor model)
// Some vendors have multiple accounts (complex vendor model)
export const sampleVendorAccounts: Record<string, VendorAccount[]> = {
  // Office Depot - regional supply accounts
  'vendor-001': [
    {
      id: 'va-001-001',
      vendorId: 'vendor-001',
      vendorCode: 'OD-001-01',
      accountNumber: 'OD-US-EAST',
      accountName: 'US East Coast Supply',
      orgUnitId: 'org-002',
      orgUnit: {
        id: 'org-002',
        code: 'NY-HQ',
        name: 'New York Headquarters',
        path: '/GLOBAL/NY-HQ',
      },
      paymentTermId: 'pt-va-001-001',
      paymentTerm: {
        id: 'pt-va-001-001',
        code: 'NET30',
        description: 'Net 30 Days',
        calculationBasis: 'InvoiceDate',
        dueDays: 30,
        createdAt: new Date(2023, 1, 10).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: 'ba-005',
      contacts: [
        {
          id: 'vac-001-001',
          contactId: 'con-005',
          isPrimary: true,
          contact: {
            name: 'David Miller',
            email: 'd.miller@officedepot.com',
            phone: '+1 (555) 222-3333',
            role: 'Account Manager',
          },
        },
      ],
      addresses: [
        {
          id: 'vaa-001-001',
          addressId: 'addr-005',
          isPrimary: true,
          type: 'Physical',
          address: {
            line1: '123 Business Park Dr',
            city: 'Austin',
            state: 'TX',
            country: 'US',
          },
        },
      ],
      isActive: true,
      createdAt: new Date(2023, 1, 10).toISOString(),
      createdById: 'system',
    },
    {
      id: 'va-001-002',
      vendorId: 'vendor-001',
      vendorCode: 'OD-001-02',
      accountNumber: 'OD-US-WEST',
      accountName: 'US West Coast Supply',
      orgUnitId: 'org-003',
      orgUnit: {
        id: 'org-003',
        code: 'SF-OFFICE',
        name: 'San Francisco Office',
        path: '/GLOBAL/SF-OFFICE',
      },
      paymentTermId: 'pt-va-001-002',
      paymentTerm: {
        id: 'pt-va-001-002',
        code: 'NET45',
        description: 'Net 45 Days',
        calculationBasis: 'InvoiceDate',
        dueDays: 45,
        createdAt: new Date(2023, 2, 1).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: null,
      contacts: [
        {
          id: 'vac-001-002',
          contactId: 'con-009',
          isPrimary: true,
          contact: {
            name: 'Mary Smith',
            email: 'mary.smith@od.com',
            phone: '+1-555-000-0001',
            role: 'Regional Sales',
          },
        },
      ],
      addresses: [],
      isActive: true,
      createdAt: new Date(2023, 2, 1).toISOString(),
      createdById: 'system',
    },
  ],
  // Melbourne Office Solutions - complex vendor with regional accounts
  'vendor-021': [
    {
      id: 'va-021-001',
      vendorId: 'vendor-021',
      vendorCode: 'MOS-001-01',
      accountNumber: 'SYD-METRO',
      accountName: 'Sydney Metropolitan',
      orgUnitId: 'org-007',
      orgUnit: {
        id: 'org-007',
        code: 'SYD-OFFICE',
        name: 'Sydney Office',
        path: '/APAC-PTY/SYD-OFFICE',
      },
      paymentTermId: 'pt-va-021-001',
      paymentTerm: {
        id: 'pt-va-021-001',
        code: 'NET30',
        description: 'Net 30 Days',
        calculationBasis: 'InvoiceDate',
        dueDays: 30,
        createdAt: new Date(2023, 0, 1).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: 'ba-024',
      contacts: [
        {
          id: 'vac-021-001',
          contactId: 'con-029',
          isPrimary: true,
          contact: {
            name: 'Emma Thompson',
            email: 'emma.thompson@melbourneoffice.com.au',
            phone: '+61 3 9123 4567',
            role: 'Account Manager',
          },
        },
      ],
      addresses: [
        {
          id: 'vaa-021-001',
          addressId: 'addr-025',
          isPrimary: true,
          type: 'Physical',
          address: {
            line1: '123 Collins Street',
            city: 'Melbourne',
            state: 'VIC',
            postalCode: '3000',
            country: 'AU',
          },
        },
      ],
      isActive: true,
      createdAt: new Date(2023, 1, 1).toISOString(),
      createdById: 'system',
    },
    {
      id: 'va-021-002',
      vendorId: 'vendor-021',
      vendorCode: 'MOS-001-02',
      accountNumber: 'NSW-REGIONAL',
      accountName: 'NSW Regional',
      orgUnitId: 'org-006',
      orgUnit: {
        id: 'org-006',
        code: 'APAC-PTY',
        name: 'Asia Pacific Technologies Pty Ltd',
        path: '/APAC-PTY',
      },
      paymentTermId: 'pt-va-021-002',
      paymentTerm: {
        id: 'pt-va-021-002',
        code: 'EOM30',
        description: 'End of Month + 30 Days',
        calculationBasis: 'EndOfMonth',
        isEndOfMonth: true,
        daysAfterEndOfMonth: 30,
        createdAt: new Date(2023, 0, 1).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: null,
      contacts: [
        {
          id: 'vac-021-002',
          contactId: 'con-030',
          isPrimary: true,
          contact: {
            name: "James O'Brien",
            email: 'james.obrien@melbourneoffice.com.au',
            phone: '+61 3 9123 4568',
            role: 'Finance Manager',
          },
        },
      ],
      addresses: [
        {
          id: 'vaa-021-002',
          addressId: 'addr-026',
          isPrimary: true,
          type: 'Mailing',
          address: {
            line1: 'PO Box 5678',
            city: 'Melbourne',
            state: 'VIC',
            postalCode: '3001',
            country: 'AU',
          },
        },
      ],
      isActive: true,
      createdAt: new Date(2023, 1, 15).toISOString(),
      createdById: 'system',
    },
  ],
  // Dell Technologies - complex vendor with regional accounts
  'vendor-002': [
    {
      id: 'va-002-001',
      vendorId: 'vendor-002',
      vendorCode: 'DELL-001-01',
      accountNumber: 'DELL-ENT-US',
      accountName: 'Dell Enterprise US',
      orgUnitId: 'org-001',
      orgUnit: {
        id: 'org-001',
        code: 'GLOBAL',
        name: 'Global Technologies Inc.',
        path: '/GLOBAL',
      },
      paymentTermId: 'pt-va-002-001',
      paymentTerm: {
        id: 'pt-va-002-001',
        code: 'NET30',
        description: 'Net 30 Days',
        calculationBasis: 'InvoiceDate',
        dueDays: 30,
        createdAt: new Date(2023, 0, 1).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: 'ba-004',
      contacts: [
        {
          id: 'vac-002-001',
          contactId: 'con-006',
          isPrimary: true,
          contact: {
            name: 'Jennifer Wu',
            email: 'j.wu@dell.com',
            phone: '+1 (555) 444-5555',
            role: 'Enterprise Sales Rep',
          },
        },
      ],
      addresses: [
        {
          id: 'vaa-002-001',
          addressId: 'addr-006',
          isPrimary: true,
          type: 'Physical',
          address: {
            line1: '500 Dell Way',
            city: 'Round Rock',
            state: 'TX',
            country: 'US',
          },
        },
      ],
      isActive: true,
      createdAt: new Date(2023, 0, 15).toISOString(),
      createdById: 'system',
    },
    {
      id: 'va-002-002',
      vendorId: 'vendor-002',
      vendorCode: 'DELL-001-02',
      accountNumber: 'DELL-EU',
      accountName: 'Dell European Operations',
      orgUnitId: 'org-004',
      orgUnit: {
        id: 'org-004',
        code: 'EURO-OPS',
        name: 'European Operations Ltd',
        path: '/EURO-OPS',
      },
      paymentTermId: 'pt-va-002-002',
      paymentTerm: {
        id: 'pt-va-002-002',
        code: 'NET60',
        description: 'Net 60 Days',
        calculationBasis: 'InvoiceDate',
        dueDays: 60,
        createdAt: new Date(2023, 0, 1).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: null,
      contacts: [
        {
          id: 'vac-002-002',
          contactId: 'con-010',
          isPrimary: true,
          contact: {
            name: 'Jane Williams',
            email: 'jane.williams@dell.com',
            phone: '+1-555-001-0001',
            role: 'EU Account Manager',
          },
        },
      ],
      addresses: [],
      isActive: true,
      createdAt: new Date(2023, 2, 1).toISOString(),
      createdById: 'system',
    },
  ],
  // Global Logistics Partners - complex vendor with service accounts
  'vendor-005': [
    {
      id: 'va-005-001',
      vendorId: 'vendor-005',
      vendorCode: 'GLP-001-01',
      accountNumber: 'GLP-FREIGHT',
      accountName: 'Freight Services',
      orgUnitId: null,
      orgUnit: null,
      paymentTermId: 'pt-va-005-001',
      paymentTerm: {
        id: 'pt-va-005-001',
        code: 'NET45',
        description: 'Net 45 Days',
        calculationBasis: 'InvoiceDate',
        dueDays: 45,
        createdAt: new Date(2023, 0, 1).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: 'ba-010',
      contacts: [],
      addresses: [],
      isActive: true,
      createdAt: new Date(2023, 3, 1).toISOString(),
      createdById: 'system',
    },
    {
      id: 'va-005-002',
      vendorId: 'vendor-005',
      vendorCode: 'GLP-001-02',
      accountNumber: 'GLP-WAREHOUSE',
      accountName: 'Warehousing Services',
      orgUnitId: null,
      orgUnit: null,
      paymentTermId: null,
      paymentTerm: null,
      preferredBankAccountId: null,
      contacts: [],
      addresses: [],
      isActive: true,
      createdAt: new Date(2023, 3, 15).toISOString(),
      createdById: 'system',
    },
    {
      id: 'va-005-003',
      vendorId: 'vendor-005',
      vendorCode: 'GLP-001-03',
      accountNumber: 'GLP-CUSTOMS',
      accountName: 'Customs Brokerage',
      orgUnitId: 'org-004',
      orgUnit: {
        id: 'org-004',
        code: 'EURO-OPS',
        name: 'European Operations Ltd',
        path: '/EURO-OPS',
      },
      paymentTermId: null,
      paymentTerm: null,
      preferredBankAccountId: null,
      contacts: [
        {
          id: 'vac-005-001',
          contactId: 'con-016',
          isPrimary: true,
          contact: {
            name: 'James Wilson',
            email: 'j.wilson@globallogistics.co.uk',
            phone: '+44 20 7946 0958',
            role: 'Customs Manager',
          },
        },
      ],
      addresses: [
        {
          id: 'vaa-005-001',
          addressId: 'addr-003',
          isPrimary: true,
          type: 'Physical',
          address: {
            line1: '10 Downing Street',
            city: 'London',
            country: 'GB',
          },
        },
      ],
      isActive: false,
      createdAt: new Date(2023, 5, 1).toISOString(),
      createdById: 'system',
    },
  ],
  // Acme Construction - project-based accounts
  'vendor-004': [
    {
      id: 'va-004-001',
      vendorId: 'vendor-004',
      vendorCode: 'ACM-001-01',
      accountNumber: 'ACM-COMMERCIAL',
      accountName: 'Commercial Projects',
      orgUnitId: 'org-001',
      orgUnit: {
        id: 'org-001',
        code: 'GLOBAL',
        name: 'Global Technologies Inc.',
        path: '/GLOBAL',
      },
      paymentTermId: 'pt-va-004-001',
      paymentTerm: {
        id: 'pt-va-004-001',
        code: 'NET60',
        description: 'Net 60 Days',
        calculationBasis: 'InvoiceDate',
        dueDays: 60,
        createdAt: new Date(2023, 2, 1).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: 'ba-007',
      contacts: [
        {
          id: 'vac-004-001',
          contactId: 'con-008',
          isPrimary: true,
          contact: {
            name: 'Lisa Anderson',
            email: 'l.anderson@acmeconst.com',
            phone: '+1 (555) 888-9999',
            role: 'Project Manager',
          },
        },
      ],
      addresses: [
        {
          id: 'vaa-004-001',
          addressId: 'addr-008',
          isPrimary: true,
          type: 'Physical',
          address: {
            line1: '321 Construction Blvd',
            city: 'Los Angeles',
            state: 'CA',
            country: 'US',
          },
        },
      ],
      isActive: true,
      createdAt: new Date(2023, 2, 1).toISOString(),
      createdById: 'system',
    },
    {
      id: 'va-004-002',
      vendorId: 'vendor-004',
      vendorCode: 'ACM-001-02',
      accountNumber: 'ACM-RESIDENTIAL',
      accountName: 'Residential Projects',
      orgUnitId: 'org-002',
      orgUnit: {
        id: 'org-002',
        code: 'NY-HQ',
        name: 'New York Headquarters',
        path: '/GLOBAL/NY-HQ',
      },
      paymentTermId: 'pt-va-004-002',
      paymentTerm: {
        id: 'pt-va-004-002',
        code: 'NET30',
        description: 'Net 30 Days',
        calculationBasis: 'InvoiceDate',
        dueDays: 30,
        createdAt: new Date(2023, 3, 1).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: null,
      contacts: [],
      addresses: [],
      isActive: true,
      createdAt: new Date(2023, 3, 1).toISOString(),
      createdById: 'system',
    },
    {
      id: 'va-004-003',
      vendorId: 'vendor-004',
      vendorCode: 'ACM-001-03',
      accountNumber: 'ACM-GOVT',
      accountName: 'Government Contracts',
      orgUnitId: null,
      orgUnit: null,
      paymentTermId: 'pt-va-004-003',
      paymentTerm: {
        id: 'pt-va-004-003',
        code: 'EOM30',
        description: 'End of Month + 30 Days',
        calculationBasis: 'EndOfMonth',
        isEndOfMonth: true,
        daysAfterEndOfMonth: 30,
        createdAt: new Date(2023, 4, 1).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: 'ba-007',
      contacts: [],
      addresses: [],
      isActive: true,
      createdAt: new Date(2023, 4, 1).toISOString(),
      createdById: 'system',
    },
  ],
  // TechSupply Co. - regional accounts
  'vendor-006': [
    {
      id: 'va-006-001',
      vendorId: 'vendor-006',
      vendorCode: 'TSC-001-01',
      accountNumber: 'TSC-EU-OPS',
      accountName: 'European Operations',
      orgUnitId: 'org-004',
      orgUnit: {
        id: 'org-004',
        code: 'EURO-OPS',
        name: 'European Operations Ltd',
        path: '/EURO-OPS',
      },
      paymentTermId: 'pt-va-006-001',
      paymentTerm: {
        id: 'pt-va-006-001',
        code: 'NET30',
        description: 'Net 30 Days',
        calculationBasis: 'InvoiceDate',
        dueDays: 30,
        createdAt: new Date(2023, 1, 15).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: 'ba-009',
      contacts: [
        {
          id: 'vac-006-001',
          contactId: 'con-015',
          isPrimary: true,
          contact: {
            name: 'Carlos Martinez',
            email: 'carlos@techsupply.de',
            phone: '+49 30 12345678',
            role: 'Sales Manager',
          },
        },
      ],
      addresses: [
        {
          id: 'vaa-006-001',
          addressId: 'addr-010',
          isPrimary: true,
          type: 'Physical',
          address: {
            line1: 'Hauptstrasse 123',
            city: 'Berlin',
            country: 'DE',
          },
        },
      ],
      isActive: true,
      createdAt: new Date(2023, 1, 15).toISOString(),
      createdById: 'system',
    },
    {
      id: 'va-006-002',
      vendorId: 'vendor-006',
      vendorCode: 'TSC-001-02',
      accountNumber: 'TSC-APAC',
      accountName: 'Asia Pacific',
      orgUnitId: 'org-006',
      orgUnit: {
        id: 'org-006',
        code: 'APAC-PTY',
        name: 'Asia Pacific Technologies Pty Ltd',
        path: '/APAC-PTY',
      },
      paymentTermId: 'pt-va-006-002',
      paymentTerm: {
        id: 'pt-va-006-002',
        code: 'NET45',
        description: 'Net 45 Days',
        calculationBasis: 'InvoiceDate',
        dueDays: 45,
        createdAt: new Date(2023, 3, 1).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: null,
      contacts: [],
      addresses: [],
      isActive: true,
      createdAt: new Date(2023, 3, 1).toISOString(),
      createdById: 'system',
    },
  ],
  // Pacific Manufacturing - product-line accounts
  'vendor-008': [
    {
      id: 'va-008-001',
      vendorId: 'vendor-008',
      vendorCode: 'PMF-001-01',
      accountNumber: 'PMF-COMPONENTS',
      accountName: 'Components & Parts',
      orgUnitId: 'org-001',
      orgUnit: {
        id: 'org-001',
        code: 'GLOBAL',
        name: 'Global Technologies Inc.',
        path: '/GLOBAL',
      },
      paymentTermId: 'pt-va-008-001',
      paymentTerm: {
        id: 'pt-va-008-001',
        code: 'NET30',
        description: 'Net 30 Days',
        calculationBasis: 'InvoiceDate',
        dueDays: 30,
        createdAt: new Date(2023, 0, 20).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: 'ba-008',
      contacts: [
        {
          id: 'vac-008-001',
          contactId: 'con-011',
          isPrimary: true,
          contact: {
            name: 'Mary Rodriguez',
            email: 'mary.rodriguez@pmf.com',
            phone: '+1-555-007-0000',
            role: 'Primary Contact',
          },
        },
      ],
      addresses: [
        {
          id: 'vaa-008-001',
          addressId: 'addr-009',
          isPrimary: true,
          type: 'Physical',
          address: {
            line1: '1-2-3 Shibuya',
            city: 'Tokyo',
            country: 'JP',
          },
        },
      ],
      isActive: true,
      createdAt: new Date(2023, 0, 20).toISOString(),
      createdById: 'system',
    },
    {
      id: 'va-008-002',
      vendorId: 'vendor-008',
      vendorCode: 'PMF-001-02',
      accountNumber: 'PMF-FINISHED',
      accountName: 'Finished Goods',
      orgUnitId: null,
      orgUnit: null,
      paymentTermId: 'pt-va-008-002',
      paymentTerm: {
        id: 'pt-va-008-002',
        code: 'EOM45',
        description: 'End of Month + 45 Days',
        calculationBasis: 'EndOfMonth',
        isEndOfMonth: true,
        daysAfterEndOfMonth: 45,
        createdAt: new Date(2023, 2, 10).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: null,
      contacts: [],
      addresses: [],
      isActive: true,
      createdAt: new Date(2023, 2, 10).toISOString(),
      createdById: 'system',
    },
  ],
  // Metro Cleaning Services - single account
  'vendor-009': [
    {
      id: 'va-009-001',
      vendorId: 'vendor-009',
      vendorCode: 'MCS-001-01',
      accountNumber: 'MCS-NYC',
      accountName: 'NYC Metro Area',
      orgUnitId: 'org-002',
      orgUnit: {
        id: 'org-002',
        code: 'NY-HQ',
        name: 'New York Headquarters',
        path: '/GLOBAL/NY-HQ',
      },
      paymentTermId: 'pt-va-009-001',
      paymentTerm: {
        id: 'pt-va-009-001',
        code: 'NET14',
        description: 'Net 14 Days',
        calculationBasis: 'InvoiceDate',
        dueDays: 14,
        createdAt: new Date(2023, 4, 1).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: 'ba-012',
      contacts: [
        {
          id: 'vac-009-001',
          contactId: 'con-018',
          isPrimary: true,
          contact: {
            name: 'Michael Johnson',
            email: 'm.johnson@metrocleaning.com',
            phone: '+1 (212) 555-0199',
            role: 'Service Manager',
          },
        },
      ],
      addresses: [
        {
          id: 'vaa-009-001',
          addressId: 'addr-014',
          isPrimary: true,
          type: 'Physical',
          address: {
            line1: '777 Cleaning Ave',
            city: 'New York',
            state: 'NY',
            country: 'US',
          },
        },
      ],
      isActive: true,
      createdAt: new Date(2023, 4, 1).toISOString(),
      createdById: 'system',
    },
  ],
  // Elite Consulting Group - service-line accounts
  'vendor-010': [
    {
      id: 'va-010-001',
      vendorId: 'vendor-010',
      vendorCode: 'ECG-001-01',
      accountNumber: 'ECG-STRATEGY',
      accountName: 'Strategy Consulting',
      orgUnitId: null,
      orgUnit: null,
      paymentTermId: 'pt-va-010-001',
      paymentTerm: {
        id: 'pt-va-010-001',
        code: 'NET30',
        description: 'Net 30 Days',
        calculationBasis: 'InvoiceDate',
        dueDays: 30,
        createdAt: new Date(2023, 1, 1).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: 'ba-013',
      contacts: [
        {
          id: 'vac-010-001',
          contactId: 'con-019',
          isPrimary: true,
          contact: {
            name: 'Patricia Davis',
            email: 'p.davis@eliteconsulting.com',
            phone: '+1 (415) 555-0177',
            role: 'Senior Consultant',
          },
        },
      ],
      addresses: [
        {
          id: 'vaa-010-001',
          addressId: 'addr-015',
          isPrimary: true,
          type: 'Physical',
          address: {
            line1: '999 Consulting Plaza',
            city: 'San Francisco',
            state: 'CA',
            country: 'US',
          },
        },
      ],
      isActive: true,
      createdAt: new Date(2023, 1, 1).toISOString(),
      createdById: 'system',
    },
    {
      id: 'va-010-002',
      vendorId: 'vendor-010',
      vendorCode: 'ECG-001-02',
      accountNumber: 'ECG-TECH',
      accountName: 'Technology Advisory',
      orgUnitId: 'org-003',
      orgUnit: {
        id: 'org-003',
        code: 'SF-OFFICE',
        name: 'San Francisco Office',
        path: '/GLOBAL/SF-OFFICE',
      },
      paymentTermId: 'pt-va-010-002',
      paymentTerm: {
        id: 'pt-va-010-002',
        code: 'NET45',
        description: 'Net 45 Days',
        calculationBasis: 'InvoiceDate',
        dueDays: 45,
        discountPercent: 2,
        discountDays: 10,
        createdAt: new Date(2023, 3, 15).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: null,
      contacts: [],
      addresses: [],
      isActive: true,
      createdAt: new Date(2023, 3, 15).toISOString(),
      createdById: 'system',
    },
  ],
  // Northern Supplies Ltd - single account
  'vendor-011': [
    {
      id: 'va-011-001',
      vendorId: 'vendor-011',
      vendorCode: 'NSL-001-01',
      accountNumber: 'NSL-WEST-CA',
      accountName: 'Western Canada Distribution',
      orgUnitId: 'org-007',
      orgUnit: {
        id: 'org-007',
        code: 'SYD-OFFICE',
        name: 'Sydney Office',
        path: '/APAC-PTY/SYD-OFFICE',
      },
      paymentTermId: 'pt-va-011-001',
      paymentTerm: {
        id: 'pt-va-011-001',
        code: 'NET30',
        description: 'Net 30 Days',
        calculationBasis: 'InvoiceDate',
        dueDays: 30,
        createdAt: new Date(2023, 5, 1).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: 'ba-014',
      contacts: [
        {
          id: 'vac-011-001',
          contactId: 'con-020',
          isPrimary: true,
          contact: {
            name: 'Robert Thompson',
            email: 'r.thompson@northernsupplies.ca',
            phone: '+1 (416) 555-0144',
            role: 'Sales Representative',
          },
        },
      ],
      addresses: [
        {
          id: 'vaa-011-001',
          addressId: 'addr-016',
          isPrimary: true,
          type: 'Physical',
          address: {
            line1: '444 Supply Road',
            city: 'Vancouver',
            state: 'BC',
            country: 'CA',
          },
        },
      ],
      isActive: true,
      createdAt: new Date(2023, 5, 1).toISOString(),
      createdById: 'system',
    },
  ],
  // Continental Foods - product category accounts
  'vendor-013': [
    {
      id: 'va-013-001',
      vendorId: 'vendor-013',
      vendorCode: 'CFD-001-01',
      accountNumber: 'CFD-FRESH',
      accountName: 'Fresh Produce',
      orgUnitId: 'org-004',
      orgUnit: {
        id: 'org-004',
        code: 'EURO-OPS',
        name: 'European Operations Ltd',
        path: '/EURO-OPS',
      },
      paymentTermId: 'pt-va-013-001',
      paymentTerm: {
        id: 'pt-va-013-001',
        code: 'NET7',
        description: 'Net 7 Days',
        calculationBasis: 'InvoiceDate',
        dueDays: 7,
        createdAt: new Date(2023, 4, 10).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: 'ba-016',
      contacts: [
        {
          id: 'vac-013-001',
          contactId: 'con-022',
          isPrimary: true,
          contact: {
            name: 'Pierre Dubois',
            email: 'p.dubois@continentalfoods.fr',
            phone: '+33 1 42 86 82 00',
            role: 'Export Manager',
          },
        },
      ],
      addresses: [
        {
          id: 'vaa-013-001',
          addressId: 'addr-018',
          isPrimary: true,
          type: 'Physical',
          address: {
            line1: '66 Rue de la Paix',
            city: 'Paris',
            country: 'FR',
          },
        },
      ],
      isActive: true,
      createdAt: new Date(2023, 4, 10).toISOString(),
      createdById: 'system',
    },
    {
      id: 'va-013-002',
      vendorId: 'vendor-013',
      vendorCode: 'CFD-001-02',
      accountNumber: 'CFD-PACKAGED',
      accountName: 'Packaged Goods',
      orgUnitId: null,
      orgUnit: null,
      paymentTermId: 'pt-va-013-002',
      paymentTerm: {
        id: 'pt-va-013-002',
        code: 'NET30',
        description: 'Net 30 Days',
        calculationBasis: 'InvoiceDate',
        dueDays: 30,
        createdAt: new Date(2023, 5, 1).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: null,
      contacts: [],
      addresses: [],
      isActive: true,
      createdAt: new Date(2023, 5, 1).toISOString(),
      createdById: 'system',
    },
  ],
  // Swift Transportation - service type accounts
  'vendor-016': [
    {
      id: 'va-016-001',
      vendorId: 'vendor-016',
      vendorCode: 'SWT-001-01',
      accountNumber: 'SWT-DOMESTIC',
      accountName: 'Domestic Freight',
      orgUnitId: null,
      orgUnit: null,
      paymentTermId: 'pt-va-016-001',
      paymentTerm: {
        id: 'pt-va-016-001',
        code: 'NET14',
        description: 'Net 14 Days',
        calculationBasis: 'InvoiceDate',
        dueDays: 14,
        createdAt: new Date(2023, 2, 15).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: 'ba-019',
      contacts: [
        {
          id: 'vac-016-001',
          contactId: 'con-024',
          isPrimary: true,
          contact: {
            name: 'Barbara White',
            email: 'b.white@swifttrans.com',
            phone: '+1 (718) 555-0155',
            role: 'Logistics Coordinator',
          },
        },
      ],
      addresses: [
        {
          id: 'vaa-016-001',
          addressId: 'addr-020',
          isPrimary: true,
          type: 'Physical',
          address: {
            line1: '888 Transport Way',
            city: 'New York',
            state: 'NY',
            country: 'US',
          },
        },
      ],
      isActive: true,
      createdAt: new Date(2023, 2, 15).toISOString(),
      createdById: 'system',
    },
    {
      id: 'va-016-002',
      vendorId: 'vendor-016',
      vendorCode: 'SWT-001-02',
      accountNumber: 'SWT-INTL',
      accountName: 'International Shipping',
      orgUnitId: 'org-004',
      orgUnit: {
        id: 'org-004',
        code: 'EURO-OPS',
        name: 'European Operations Ltd',
        path: '/EURO-OPS',
      },
      paymentTermId: 'pt-va-016-002',
      paymentTerm: {
        id: 'pt-va-016-002',
        code: 'NET45',
        description: 'Net 45 Days',
        calculationBasis: 'InvoiceDate',
        dueDays: 45,
        createdAt: new Date(2023, 4, 1).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: null,
      contacts: [],
      addresses: [],
      isActive: true,
      createdAt: new Date(2023, 4, 1).toISOString(),
      createdById: 'system',
    },
  ],
  // Premium Office Furniture - single EU account
  'vendor-017': [
    {
      id: 'va-017-001',
      vendorId: 'vendor-017',
      vendorCode: 'POF-001-01',
      accountNumber: 'POF-EU-SHOW',
      accountName: 'EU Showroom & Sales',
      orgUnitId: 'org-004',
      orgUnit: {
        id: 'org-004',
        code: 'EURO-OPS',
        name: 'European Operations Ltd',
        path: '/EURO-OPS',
      },
      paymentTermId: 'pt-va-017-001',
      paymentTerm: {
        id: 'pt-va-017-001',
        code: 'NET60',
        description: 'Net 60 Days',
        calculationBasis: 'InvoiceDate',
        dueDays: 60,
        discountPercent: 3,
        discountDays: 15,
        createdAt: new Date(2023, 3, 1).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: 'ba-020',
      contacts: [
        {
          id: 'vac-017-001',
          contactId: 'con-025',
          isPrimary: true,
          contact: {
            name: 'Giovanni Rossi',
            email: 'g.rossi@premiumfurniture.it',
            phone: '+39 02 1234 5678',
            role: 'Sales Director',
          },
        },
      ],
      addresses: [
        {
          id: 'vaa-017-001',
          addressId: 'addr-021',
          isPrimary: true,
          type: 'Physical',
          address: {
            line1: 'Via Milano 45',
            city: 'Milan',
            country: 'IT',
          },
        },
      ],
      isActive: true,
      createdAt: new Date(2023, 3, 1).toISOString(),
      createdById: 'system',
    },
  ],
  // CloudTech Solutions - service accounts
  'vendor-018': [
    {
      id: 'va-018-001',
      vendorId: 'vendor-018',
      vendorCode: 'CTS-001-01',
      accountNumber: 'CTS-CLOUD',
      accountName: 'Cloud Infrastructure',
      orgUnitId: null,
      orgUnit: null,
      paymentTermId: 'pt-va-018-001',
      paymentTerm: {
        id: 'pt-va-018-001',
        code: 'IMM',
        description: 'Immediate / Due on Receipt',
        calculationBasis: 'Immediate',
        createdAt: new Date(2023, 5, 1).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: 'ba-021',
      contacts: [
        {
          id: 'vac-018-001',
          contactId: 'con-026',
          isPrimary: true,
          contact: {
            name: 'Jennifer Lee',
            email: 'j.lee@cloudtech.com',
            phone: '+1 (646) 555-0122',
            role: 'Cloud Solutions Architect',
          },
        },
      ],
      addresses: [
        {
          id: 'vaa-018-001',
          addressId: 'addr-022',
          isPrimary: true,
          type: 'Physical',
          address: {
            line1: '111 Cloud Street',
            city: 'New York',
            state: 'NY',
            country: 'US',
          },
        },
      ],
      isActive: true,
      createdAt: new Date(2023, 5, 1).toISOString(),
      createdById: 'system',
    },
    {
      id: 'va-018-002',
      vendorId: 'vendor-018',
      vendorCode: 'CTS-001-02',
      accountNumber: 'CTS-MANAGED',
      accountName: 'Managed Services',
      orgUnitId: 'org-003',
      orgUnit: {
        id: 'org-003',
        code: 'SF-OFFICE',
        name: 'San Francisco Office',
        path: '/GLOBAL/SF-OFFICE',
      },
      paymentTermId: 'pt-va-018-002',
      paymentTerm: {
        id: 'pt-va-018-002',
        code: 'EOM',
        description: 'End of Month',
        calculationBasis: 'EndOfMonth',
        isEndOfMonth: true,
        daysAfterEndOfMonth: 0,
        createdAt: new Date(2023, 6, 1).toISOString(),
        createdById: 'system',
      },
      preferredBankAccountId: null,
      contacts: [],
      addresses: [],
      isActive: false,
      createdAt: new Date(2023, 6, 1).toISOString(),
      createdById: 'system',
    },
  ],
}

// Full Vendor records aligned with C# domain model
// This is the single source of truth — no separate mock files needed
export const allVendors: Vendor[] = sampleVendors.map((v) => {
  const vendorDefaults: Record<
    string,
    {
      orgUnitId: string
      taxNumber: string
      status: VendorStatus
      description?: string
      website?: string
      note?: string
    }
  > = {
    'vendor-001': {
      orgUnitId: '',
      taxNumber: 'TAX-OD-12345',
      status: VendorStatus.Active,
      description: 'Office supplies and equipment provider',
    },
    'vendor-002': {
      orgUnitId: '',
      taxNumber: 'TAX-DELL-67890',
      status: VendorStatus.Active,
      description: 'IT hardware and solutions',
      website: 'https://dell.com',
    },
    'vendor-003': {
      orgUnitId: 'org-001',
      taxNumber: 'TAX-PW-11111',
      status: VendorStatus.Active,
      description: 'Commercial printing services',
    },
    'vendor-004': {
      orgUnitId: '',
      taxNumber: 'TAX-ACM-22222',
      status: VendorStatus.Active,
      description: 'General construction and contracting',
    },
    'vendor-005': {
      orgUnitId: 'org-004',
      taxNumber: 'TAX-GLP-33333',
      status: VendorStatus.Active,
      description: 'International logistics and freight',
    },
    'vendor-006': {
      orgUnitId: '',
      taxNumber: 'TAX-TSC-44444',
      status: VendorStatus.Active,
    },
    'vendor-007': {
      orgUnitId: '',
      taxNumber: 'TAX-GES-55555',
      status: VendorStatus.Active,
      description: 'Renewable energy solutions',
      website: 'https://greenenergy.com',
    },
    'vendor-008': {
      orgUnitId: 'org-001',
      taxNumber: 'TAX-PMF-66666',
      status: VendorStatus.Active,
    },
    'vendor-009': {
      orgUnitId: 'org-002',
      taxNumber: 'TAX-MCS-77777',
      status: VendorStatus.Active,
    },
    'vendor-010': {
      orgUnitId: '',
      taxNumber: 'TAX-ECG-88888',
      status: VendorStatus.Active,
    },
    'vendor-011': {
      orgUnitId: 'org-007',
      taxNumber: 'TAX-NSL-99999',
      status: VendorStatus.Active,
    },
    'vendor-012': {
      orgUnitId: 'org-003',
      taxNumber: 'TAX-ASS-10101',
      status: VendorStatus.Active,
    },
    'vendor-013': {
      orgUnitId: 'org-004',
      taxNumber: 'TAX-CFD-20202',
      status: VendorStatus.PendingReview,
    },
    'vendor-014': {
      orgUnitId: '',
      taxNumber: 'TAX-DMP-30303',
      status: VendorStatus.PendingReview,
    },
    'vendor-015': {
      orgUnitId: '',
      taxNumber: 'TAX-IPW-40404',
      status: VendorStatus.PendingReview,
    },
    'vendor-016': {
      orgUnitId: '',
      taxNumber: 'TAX-SWT-50505',
      status: VendorStatus.Active,
    },
    'vendor-017': {
      orgUnitId: 'org-004',
      taxNumber: 'TAX-POF-60606',
      status: VendorStatus.Active,
    },
    'vendor-018': {
      orgUnitId: '',
      taxNumber: 'TAX-CTS-70707',
      status: VendorStatus.Draft,
    },
    'vendor-019': {
      orgUnitId: 'org-002',
      taxNumber: 'TAX-RMC-80808',
      status: VendorStatus.Inactive,
    },
    'vendor-020': {
      orgUnitId: 'org-004',
      taxNumber: 'TAX-GTP-90909',
      status: VendorStatus.Inactive,
    },
    'vendor-021': {
      orgUnitId: '',
      taxNumber: 'ABN-12-345-678-901',
      status: VendorStatus.Active,
      description: 'Office supplies for Australian operations',
      website: 'https://melbourneoffice.com.au',
    },
  }

  const defaults = vendorDefaults[v.id] || {
    orgUnitId: '',
    taxNumber: '',
    status: VendorStatus.Active,
  }

  const countryInfo = countryMap[v.country] || {
    id: 'country-unknown',
    code: 'XX',
  }

  return {
    id: v.id,
    name: v.name,
    code: v.code,
    type: typeMap[v.type] || VendorType.Other,
    taxNumber: defaults.taxNumber,
    countryId: countryInfo.id,
    country: {
      id: countryInfo.id,
      name: v.country,
      code: countryInfo.code,
    },
    description: defaults.description || '',
    note: defaults.note || '',
    website: defaults.website || '',
    orgUnitId: defaults.orgUnitId,
    orgUnit: null, // Populated by VendorManagement when needed
    paymentTermId: null,
    paymentTerm: null,
    contacts: [],
    addresses: [],
    bankAccounts: [],
    documents: [],
    registrations: [],
    history: [],
    accounts: sampleVendorAccounts[v.id] || [],
    status: defaults.status,
    onboardedAt:
      defaults.status === VendorStatus.Active
        ? new Date(2023, 0, 15).toISOString()
        : null,
    createdAt: new Date(2023, 0, 1).toISOString(),
    createdById: 'system',
    createdBy: { id: 'system', name: 'System' },
    updatedAt: null,
    updatedById: null,
    updatedBy: null,
  }
})
